import { atom, WritableAtom } from 'jotai';
import { FieldValidator, validateField } from './formValidation';

export type FieldAtomConfig<T, TWrite = T> = {
  valueAtom: WritableAtom<T, [TWrite], void>;
  validator?: FieldValidator<T>;
};

export type ValidatedFieldAtoms<T, TWrite = T> = {
  errorAtom: WritableAtom<string | null, [string | null], void>;
  touchedAtom: WritableAtom<boolean, [boolean], void>;
  validator?: FieldValidator<T>;
  valueAtom: WritableAtom<T, [TWrite], void>;
};

/**
 * Creates a set of atoms for a validated form field
 * Returns value atom, error atom, and touched atom
 */
export const createValidatedFieldAtoms = <T, TWrite = T>(
  config: FieldAtomConfig<T, TWrite>
): ValidatedFieldAtoms<T, TWrite> => {
  const errorAtom = atom<string | null>(null);
  const touchedAtom = atom<boolean>(false);

  return {
    errorAtom,
    touchedAtom,
    validator: config.validator,
    valueAtom: config.valueAtom,
  };
};

/**
 * Validates a field and updates its error atom
 */
export const validateFieldAtom = <T, TWrite = T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get: <V>(atom: WritableAtom<V, unknown[], unknown>) => V,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  set: <V, Arguments extends unknown[], Result>(
    atom: WritableAtom<V, Arguments, Result>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ...arguments_: Arguments
  ) => Result,
  fieldAtoms: ValidatedFieldAtoms<T, TWrite>
): boolean => {
  const value = get(fieldAtoms.valueAtom as any);
  const validationResult = validateField(value, fieldAtoms.validator);

  set(fieldAtoms.errorAtom, validationResult.errorMessage);
  set(fieldAtoms.touchedAtom, true);

  return validationResult.isValid;
};

/**
 * Registry to store all validated field atoms for form-level validation
 */
export class FormFieldRegistry {
  private readonly fields: Map<string, ValidatedFieldAtoms<any>> = new Map();

  register<T>(fieldName: string, fieldAtoms: ValidatedFieldAtoms<T>): void {
    this.fields.set(fieldName, fieldAtoms);
  }

  unregister(fieldName: string): void {
    this.fields.delete(fieldName);
  }

  getAllFields(): Map<string, ValidatedFieldAtoms<any>> {
    return this.fields;
  }

  clear(): void {
    this.fields.clear();
  }
}
